import React, { useRef, useLayoutEffect } from 'react';
import { Center, Checkbox, VisuallyHidden } from '@chakra-ui/react';
import { GetColumnInput, IColumn } from '../types';

import { ReactGridColumn } from '../../types';
type SharedInputProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'disabled' | 'tabIndex' | 'onClick' | 'aria-label' | 'aria-labelledby'
>;

interface SelectCellFormatterProps extends SharedInputProps {
  isCellSelected?: boolean;
  value: boolean;
  onChange: (value: boolean, isShiftClick: boolean) => void;
}

export function useFocusRef<T extends HTMLOrSVGElement>(isCellSelected: boolean | undefined) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    if (!isCellSelected) return;
    ref.current?.focus({ preventScroll: true });
  }, [isCellSelected]);

  return ref;
}

export function SelectCellFormatter({ isCellSelected, disabled, onChange }: SelectCellFormatterProps) {
  useFocusRef<HTMLInputElement>(isCellSelected);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.checked, (e.nativeEvent as MouseEvent).shiftKey);
  }

  return <Checkbox size="sm" isDisabled={disabled} onChange={handleChange} />;
}

export const DataGridSelectColumn: ReactGridColumn<any, any> = {
  key: 'select-row',
  name: '',
  width: '32px',
  sortable: false,
  frozen: true,
  headerRenderer(props) {
    return (
      <SelectCellFormatter
        aria-label="Select All"
        value={props.allRowsSelected}
        onChange={props.onAllRowsSelectionChange}
      />
    );
  },
  formatter(props) {
    return (
      <SelectCellFormatter
        aria-label="Select"
        tabIndex={-1}
        isCellSelected={props.isCellSelected}
        value={props.isRowSelected}
        onClick={(event: React.SyntheticEvent) => event.stopPropagation()}
        onChange={props.onRowSelectionChange}
      />
    );
  },
  groupFormatter(props) {
    return (
      <SelectCellFormatter
        aria-label="Select Group"
        tabIndex={-1}
        isCellSelected={props.isCellSelected}
        value={props.isRowSelected}
        onChange={props.onRowSelectionChange}
        // Stop propagation to prevent row selection
        onClick={(event: React.SyntheticEvent) => event.stopPropagation()}
      />
    );
  },
};

const Select = ({
  isSelected,
  onClick,
  isIndeterminate,
  alwaysVisible,
}: {
  isSelected: boolean;
  onClick: () => void;
  isIndeterminate?: boolean;
  alwaysVisible?: boolean;
}) => {
  return (
    <Center
      className="checkContainer"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      pb={!alwaysVisible ? 2 : null}
      height={'100%'}
    >
      <Checkbox
        className="rowCheckbox"
        size="sm"
        width="4"
        isIndeterminate={isIndeterminate}
        isChecked={isSelected}
        onClick={e => {
          //The stopPropagation() method prevents propagation of the same event from being called.
          // so when you will click checkbox, href from parent <a> won't be triggered
          e.stopPropagation();
          onClick();
        }}
      >
        <VisuallyHidden>Select</VisuallyHidden>
      </Checkbox>
    </Center>
  );
};

export const SelectColumn = ({ selectedRows }: GetColumnInput): IColumn => {
  return {
    item: {
      ...DataGridSelectColumn,
      width: '32px',
      formatter(props: any) {
        return (
          <Select isSelected={props.isRowSelected} onClick={() => props.onRowSelectionChange(!props.isRowSelected)} />
        );
      },
      headerRenderer(props: any) {
        const allRowsSelected = props.allRowsSelected && Boolean(selectedRows?.length);
        const isIndeterminate = !props.allRowsSelected && Boolean(selectedRows?.length);

        return (
          <Select
            isSelected={allRowsSelected}
            onClick={() => props.onAllRowsSelectionChange(!allRowsSelected)}
            isIndeterminate={isIndeterminate}
            alwaysVisible={true}
          />
        );
      },
    },
    visible: () => true,
  };
};
